import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { getStudents, createStudent, getDistricts, downloadImportTemplate, bulkImportStudents, previewImportStudents, uploadTechInfo, exportStudents, getClinicians } from "../lib/api";
import { useAuth } from "../App";
import { BulkDocumentUpload } from "../components/students/BulkDocumentUpload";
import { downloadFromApi } from "../lib/download";
import { printPage } from "../lib/print";

export default function StudentsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useAuth();
  const [students, setStudents] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDistrict, setFilterDistrict] = useState("all");
  const [filterStatus, setFilterStatus] = useState(
    ["current", "former", "graduated", "all"].includes(searchParams.get("status")) ? searchParams.get("status") : "current"
  );
  const [filterGradYear, setFilterGradYear] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [techInfoDialogOpen, setTechInfoDialogOpen] = useState(false);
  const [techInfoUploading, setTechInfoUploading] = useState(false);
  const [techInfoResult, setTechInfoResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const importFileRef = useRef(null);
  
  const [newStudent, setNewStudent] = useState({
    first_name: "",
    last_name: "",
    middle_name: "",
    preferred_name: "",
    pronouns: "",
    dob: "",
    school_district: "",
    morning_group: "1",
    afternoon_group: "1",
    schedule_type: "full_day",
    photos_allowed: false,
    otc_medications: false,
    start_date: "",
    end_date: "",
    status: "current",
  });

  const fetchData = async (status = null) => {
    try {
      const studentsReq = status === "graduated"
        ? getStudents(null, true, null, "graduated")
        : getStudents(status === "all" ? null : status, true);
      const [studentsRes, districtsRes, cliniciansRes] = await Promise.all([
        studentsReq,
        getDistricts(),
        getClinicians(),
      ]);
      setStudents(studentsRes.data);
      setDistricts([...(districtsRes.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setClinicians(cliniciansRes.data || []);
    } catch (err) {
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(filterStatus);
  }, [filterStatus]);

  const handleCreateStudent = async (e) => {
    e.preventDefault();
    if (!newStudent.first_name || !newStudent.last_name || !newStudent.dob) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await createStudent(newStudent);
      toast.success("Student created successfully");
      setDialogOpen(false);
      setNewStudent({
        first_name: "",
        last_name: "",
        middle_name: "",
        preferred_name: "",
        pronouns: "",
        dob: "",
        school_district: "",
        morning_group: "1",
        afternoon_group: "1",
        schedule_type: "full_day",
        photos_allowed: false,
        otc_medications: false,
        start_date: "",
        end_date: "",
        status: "current",
      });
      fetchData(filterStatus);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create student");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadFromApi("/api/students/import/template", "student_import_template.xlsx");
      
      toast.success("Template downloaded!");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download template. Try using Preview & Print instead.");
    }
  };

  const handlePreviewTemplate = () => {
    const columns = [
      { name: "Student Number", required: false, example: "S001 (leave blank for new students)" },
      { name: "First Name", required: true, example: "John" },
      { name: "Last Name", required: true, example: "Smith" },
      { name: "Middle Name", required: false, example: "Michael" },
      { name: "Preferred Name", required: false, example: "Johnny" },
      { name: "Pronouns", required: false, example: "he/him" },
      { name: "Date of Birth", required: true, example: "2015-05-15" },
      { name: "Gender", required: false, example: "M or F" },
      { name: "Grade", required: false, example: "5" },
      { name: "School District", required: false, example: "District Name" },
      { name: "Start Date", required: false, example: "2025-09-01" },
      { name: "Status", required: false, example: "current or former" },
      { name: "Contact 1 Relationship", required: false, example: "Mother" },
      { name: "Contact 1 Name", required: false, example: "Jane Smith" },
      { name: "Contact 1 Phone", required: false, example: "555-123-4567" },
      { name: "Contact 1 Email", required: false, example: "jane@email.com" },
      { name: "Contact 2 Relationship", required: false, example: "Father" },
      { name: "Contact 2 Name", required: false, example: "Bob Smith" },
      { name: "Contact 2 Phone", required: false, example: "555-987-6543" },
      { name: "Contact 2 Email", required: false, example: "bob@email.com" },
      { name: "Transport Address", required: false, example: "123 Main St" },
      { name: "Transport Name", required: false, example: "Home" },
      { name: "Transportation Provider", required: false, example: "ABC Transportation" },
      { name: "Pickup Time", required: false, example: "07:30" },
      { name: "Dropoff Time", required: false, example: "14:30" },
      { name: "Photos Allowed", required: false, example: "TRUE or FALSE" },
      { name: "OTC Medications", required: false, example: "TRUE or FALSE" },
      { name: "OTC Notes", required: false, example: "Tylenol if needed" },
      { name: "Morning Group", required: false, example: "1, 2, 3, or Other" },
      { name: "Afternoon Group", required: false, example: "1 or 2" },
      { name: "Advisory Group", required: false, example: "Advisory assignment" },
    ];

    const tableRows = columns.map(col => 
      '<tr>' +
        '<td><strong>' + col.name + '</strong></td>' +
        '<td>' + (col.required ? '<span style="color:#dc2626;font-weight:bold;">Yes *</span>' : 'No') + '</td>' +
        '<td style="color:#666;font-style:italic;">' + col.example + '</td>' +
      '</tr>'
    ).join('');

    printPage({
      title: "Student Import / Update Template",
      subtitle: "Upload a spreadsheet to create new students or update existing records",
      content: `
        <div style="background:#f0f9ff;border:1px solid #0ea5e9;padding:14px;border-radius:6px;margin-bottom:16px;">
          <h3 style="color:#0369a1;margin:0 0 8px;font-size:14px;">How Smart Import Works:</h3>
          <ul style="margin:0;padding-left:20px;font-size:12px;">
            <li><strong>Update existing:</strong> Include the student number (e.g., S001) to match and update an existing record</li>
            <li><strong>Match by name:</strong> If no student number is given, the system matches by First + Last Name</li>
            <li><strong>Create new:</strong> If no match is found, a new student is created with an auto-assigned number</li>
            <li><strong>Blank cells are skipped</strong> — existing data is preserved</li>
            <li><span style="color:#dc2626;font-weight:bold;">*</span> Required fields: First Name, Last Name, DOB (for new students only)</li>
            <li>Photos Allowed and OTC Medications track a 1-year expiry from when they're signed; renew them in each student's file</li>
          </ul>
        </div>
        <table>
          <thead><tr>
            <th style="width:30%">Column Name</th>
            <th style="width:15%">Required</th>
            <th style="width:55%">Example Value</th>
          </tr></thead>
          <tbody>${tableRows}</tbody>
        </table>
      `,
      extraStyles: `
        th { background: #00305E !important; color: #fff !important; }
        tr:nth-child(even) td { background: #f9fafb; }
      `,
    });
  };

  const handlePreviewFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);
    setPreviewData(null);
    
    try {
      const res = await previewImportStudents(file);
      setPreviewData(res.data);
      setPreviewFile(file);
      
      if (res.data.error_count > 0) {
        toast.warning(`${res.data.error_count} rows have errors - review before importing`);
      } else {
        const parts = [];
        if (res.data.update_count > 0) parts.push(`${res.data.update_count} to update`);
        if (res.data.new_count > 0) parts.push(`${res.data.new_count} new`);
        toast.success(parts.join(", ") + " — ready to import");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to preview file");
      setPreviewData(null);
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const handleConfirmImport = async () => {
    if (!previewFile) return;
    
    setImporting(true);
    
    try {
      const res = await bulkImportStudents(previewFile);
      setImportResult(res.data);
      setPreviewData(null);
      setPreviewFile(null);
      
      const parts = [];
      if (res.data.imported > 0) parts.push(`${res.data.imported} created`);
      if (res.data.updated > 0) parts.push(`${res.data.updated} updated`);
      if (parts.length) {
        toast.success(parts.join(", "));
        fetchData(filterStatus);
      }
      if (res.data.errors?.length > 0) {
        toast.error(`${res.data.errors.length} rows had errors`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import students");
      setImportResult({ errors: [err.response?.data?.detail || "Import failed"] });
    } finally {
      setImporting(false);
    }
  };

  const handleCancelPreview = () => {
    setPreviewData(null);
    setPreviewFile(null);
  };

  const filteredStudents = students.filter((student) => {
    const matchesSearch = 
      student.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.preferred_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDistrict = filterDistrict === "all" || student.school_district === filterDistrict;
    const matchesGradYear = filterStatus !== "graduated" || filterGradYear === "all" ||
      (student.end_date && student.end_date.slice(0, 4) === filterGradYear);

    return matchesSearch && matchesDistrict && matchesGradYear;
  }).sort((a, b) => a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name));

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const getAge = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatDOB = (dob) => {
    if (!dob) return "DOB not set";
    try {
      return new Date(dob + 'T00:00:00').toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return dob;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentCount = students.filter(s => s.status !== 'former').length;
  const formerCount = students.filter(s => s.status === 'former').length;
  const gradYears = Array.from(new Set(students.filter(s => s.end_date).map(s => s.end_date.slice(0, 4)))).sort().reverse();

  const handlePrintOtcList = () => {
    const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const rows = filteredStudents.map((s) => {
      const name = `${s.preferred_name || s.first_name} ${s.last_name}`;
      const allowed = s.otc_medications;
      return (
        '<tr>' +
          '<td>' + esc(s.student_number || "") + '</td>' +
          '<td>' + esc(name) + '</td>' +
          '<td style="text-align:center;font-weight:600;color:' + (allowed ? '#166534' : '#b91c1c') + ';">' + (allowed ? 'YES' : 'NO') + '</td>' +
          '<td>' + esc(s.otc_notes || "") + '</td>' +
        '</tr>'
      );
    }).join("");
    printPage({
      title: "OTC Medical List",
      subtitle: "Over-the-counter medication permissions & notes",
      content: `
        <p style="font-size:12px;color:#666;margin-bottom:10px;">${filteredStudents.length} student${filteredStudents.length === 1 ? "" : "s"}${filterStatus !== "all" ? ` · ${filterStatus}` : ""}</p>
        <table>
          <thead>
            <tr><th style="width:70px;">Student #</th><th>Student Name</th><th style="width:90px;text-align:center;">OTC (Y/N)</th><th>Notes</th></tr>
          </thead>
          <tbody>${rows || '<tr><td colspan="4" style="text-align:center;color:#999;">No students</td></tr>'}</tbody>
        </table>
      `,
    });
  };

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#00305E] tracking-tight">Student Files</h1>
          <p className="text-muted-foreground mt-1">
            {filterStatus === "current" && `${students.length} current students`}
            {filterStatus === "former" && `${students.length} former students`}
            {filterStatus === "graduated" && `${filteredStudents.length} graduate${filteredStudents.length === 1 ? "" : "s"}${filterGradYear !== "all" ? ` · Class of ${filterGradYear}` : ""}`}
            {filterStatus === "all" && `${students.length} total students`}
          </p>
        </div>
        {isAdmin() && (
          <div className="flex gap-2">
            {/* Import Students Button */}
            <Dialog open={importDialogOpen} onOpenChange={(open) => {
              setImportDialogOpen(open);
              if (open) {
                // Reset state when dialog opens
                setPreviewData(null);
                setPreviewFile(null);
                setImportResult(null);
              }
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-students-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  Import from Spreadsheet
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Import Students from Spreadsheet</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <h4 className="font-medium text-blue-800 mb-2">Step 1: Prepare Your Spreadsheet</h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Download the blank template, or export your current student list to update existing records.
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={handleDownloadTemplate}
                        className="flex-1"
                        data-testid="download-template-btn"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Blank Template
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={async () => {
                          try {
                            const res = await exportStudents();
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'students_export.xlsx';
                            a.click();
                            window.URL.revokeObjectURL(url);
                            toast.success("Students exported — edit and re-upload to update");
                          } catch { toast.error("Failed to export students"); }
                        }}
                        className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-100"
                        data-testid="export-students-btn"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Export Current Students
                      </Button>
                    </div>
                    <div className="mt-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={handlePreviewTemplate}
                        className="text-xs text-blue-600"
                        data-testid="preview-template-btn"
                      >
                        View column reference
                      </Button>
                    </div>
                  </div>

                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <h4 className="font-medium text-green-800 mb-2">Step 2: Edit & Save</h4>
                    <p className="text-sm text-green-700">
                      Edit in Excel or Google Sheets. Include <strong>student numbers</strong> to update existing records. Blank cells are skipped (existing data preserved). Save as .xlsx or .csv.
                    </p>
                  </div>

                  <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                    <h4 className="font-medium text-amber-800 mb-2">Step 3: Upload & Review</h4>
                    <p className="text-sm text-amber-700 mb-3">
                      Upload your spreadsheet to preview which students will be updated vs. created new.
                    </p>
                    <input
                      type="file"
                      ref={importFileRef}
                      onChange={handlePreviewFile}
                      className="hidden"
                      accept=".xlsx,.csv"
                    />
                    {!previewData ? (
                      <Button 
                        onClick={() => {
                          if (importFileRef.current) {
                            importFileRef.current.value = '';
                            importFileRef.current.click();
                          }
                        }}
                        className="w-full bg-[#00305E] hover:bg-[#00305E]/90"
                        disabled={importing}
                        data-testid="upload-spreadsheet-btn"
                      >
                        {importing ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                            Select File to Preview
                          </>
                        )}
                      </Button>
                    ) : (
                      <p className="text-sm text-amber-700 text-center py-2">File selected - see preview below</p>
                    )}
                  </div>

                  {/* Preview Table */}
                  {previewData && (
                    <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-slate-800">Preview: {previewData.total_rows} Students</h4>
                        <div className="flex gap-3 text-sm">
                          {previewData.update_count > 0 && (
                            <span className="text-blue-600 font-medium">{previewData.update_count} updates</span>
                          )}
                          {previewData.new_count > 0 && (
                            <span className="text-green-600 font-medium">{previewData.new_count} new</span>
                          )}
                          {previewData.error_count > 0 && (
                            <span className="text-red-600 font-medium">{previewData.error_count} errors</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="max-h-72 overflow-y-auto border rounded bg-white">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 sticky top-0">
                            <tr>
                              <th className="text-left p-2 font-medium">Row</th>
                              <th className="text-left p-2 font-medium">ID</th>
                              <th className="text-left p-2 font-medium">Name</th>
                              <th className="text-left p-2 font-medium">Action</th>
                              <th className="text-left p-2 font-medium">Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.preview.map((item, idx) => (
                              <tr key={`${item.row_number}-${item.student_number || item.last_name || idx}`} className={`border-t ${item.action === 'error' ? 'bg-red-50' : item.action === 'update' ? 'bg-blue-50/30' : ''}`}
                                  data-testid={`preview-row-${idx}`}>
                                <td className="p-2 text-muted-foreground">{item.row_number}</td>
                                <td className="p-2 text-xs font-mono text-muted-foreground">{item.student_number || '-'}</td>
                                <td className="p-2 font-medium">{item.first_name} {item.last_name}</td>
                                <td className="p-2">
                                  {item.action === 'update' && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-medium">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                      Update {item.change_count > 0 ? `(${item.change_count})` : '(no changes)'}
                                    </span>
                                  )}
                                  {item.action === 'new' && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs font-medium">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                      New
                                    </span>
                                  )}
                                  {item.action === 'error' && (
                                    <span className="text-red-600 text-xs font-medium">{item.errors.join(', ')}</span>
                                  )}
                                </td>
                                <td className="p-2 text-xs">
                                  {item.action === 'update' && item.changes?.length > 0 && (
                                    <div className="space-y-0.5 max-w-xs">
                                      {item.changes.slice(0, 3).map((c) => (
                                        <div key={`${item.row_number}-${c.field}`} className="text-muted-foreground">
                                          <span className="font-medium">{c.field}</span>: <span className="line-through text-red-400">{c.old || '(empty)'}</span> → <span className="text-green-700">{c.new}</span>
                                        </div>
                                      ))}
                                      {item.changes.length > 3 && (
                                        <span className="text-muted-foreground">+{item.changes.length - 3} more changes</span>
                                      )}
                                    </div>
                                  )}
                                  {item.action === 'update' && (!item.changes || item.changes.length === 0) && (
                                    <span className="text-muted-foreground italic">No field changes detected</span>
                                  )}
                                  {item.action === 'new' && <span className="text-muted-foreground">New student record</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="flex gap-2 mt-4">
                        <Button 
                          variant="outline" 
                          onClick={handleCancelPreview}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleConfirmImport}
                          className="flex-1 bg-[#00305E] hover:bg-[#00305E]/90"
                          disabled={importing || previewData.valid_count === 0}
                          data-testid="confirm-import-btn"
                        >
                          {importing ? 'Processing...' : `Apply: ${previewData.update_count > 0 ? `${previewData.update_count} Updates` : ''}${previewData.update_count > 0 && previewData.new_count > 0 ? ' + ' : ''}${previewData.new_count > 0 ? `${previewData.new_count} New` : ''}`}
                        </Button>
                      </div>
                    </div>
                  )}

                  {importResult && (
                    <div className={`p-4 rounded-lg ${(importResult.imported > 0 || importResult.updated > 0) ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <h4 className={`font-medium mb-2 ${(importResult.imported > 0 || importResult.updated > 0) ? 'text-green-800' : 'text-red-800'}`}>
                        Import Results
                      </h4>
                      {importResult.updated > 0 && (
                        <p className="text-sm text-blue-700">&#9998; Updated {importResult.updated} existing student records</p>
                      )}
                      {importResult.imported > 0 && (
                        <p className="text-sm text-green-700">+ Created {importResult.imported} new student records</p>
                      )}
                      {importResult.errors?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm text-red-700 font-medium">Errors:</p>
                          <ul className="text-sm text-red-600 list-disc list-inside mt-1 max-h-32 overflow-y-auto">
                            {importResult.errors.map((err, i) => (
                              <li key={`import-err-${i}-${err.slice(0, 30)}`}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Tech Info Upload Button */}
            <Dialog open={techInfoDialogOpen} onOpenChange={(open) => {
              setTechInfoDialogOpen(open);
              if (open) setTechInfoResult(null);
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="upload-tech-info-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                  Upload Tech Info
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Upload Tech Info</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <p className="text-sm text-muted-foreground">Upload a spreadsheet with student emails, Chromebook numbers, and passwords. Matches students by name.</p>
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <p className="text-xs text-blue-700 mb-2">Expected columns: <strong>Student Name</strong>, <strong>Chromebook / Email Login</strong>, <strong>Chromebook #</strong>, <strong>New Password</strong>, <strong>Old Password</strong>, <strong>Staff Reset</strong></p>
                    <button type="button" onClick={async () => {
                      try {
                        const { downloadFromApi } = await import("../lib/download");
                        await downloadFromApi("/api/students/tech-info/template", "tech_info_template.xlsx");
                      } catch { toast.error("Failed to download template"); }
                    }}
                      className="text-xs text-blue-600 underline cursor-pointer" data-testid="download-tech-template">Download Template</button>
                  </div>
                  <input type="file" accept=".xlsx,.csv" className="text-sm" data-testid="tech-info-file-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setTechInfoUploading(true);
                      setTechInfoResult(null);
                      try {
                        const formData = new FormData();
                        formData.append("file", file);
                        const res = await uploadTechInfo(formData);
                        setTechInfoResult(res.data);
                        toast.success(`Tech info uploaded! Updated ${res.data.updated} student(s)`);
                        fetchData();
                      } catch (err) {
                        const detail = err.response?.data?.detail;
                        const msg = typeof detail === "string" ? detail : (Array.isArray(detail) ? (detail[0]?.msg || "Upload failed — check the file format") : "Upload failed");
                        setTechInfoResult({ error: msg });
                      } finally {
                        setTechInfoUploading(false);
                      }
                    }} />
                  {techInfoUploading && <p className="text-sm text-muted-foreground">Uploading...</p>}
                  {techInfoResult && !techInfoResult.error && (
                    <div className="p-3 rounded-lg bg-green-50 border border-green-200 space-y-2">
                      <p className="text-sm font-medium text-green-800">Upload successful! Updated {techInfoResult.updated} of {techInfoResult.total_rows} students</p>
                      {techInfoResult.not_found?.length > 0 && (
                        <div>
                          <p className="text-xs text-amber-700 font-medium">Not matched:</p>
                          {techInfoResult.not_found.map((n, i) => <p key={`not-found-${i}-${n}`} className="text-xs text-amber-600">{n}</p>)}
                        </div>
                      )}
                      <Button size="sm" className="w-full bg-[#00305E] hover:bg-[#00305E]/90 mt-2" onClick={() => setTechInfoDialogOpen(false)} data-testid="tech-upload-done-btn">
                        Done
                      </Button>
                    </div>
                  )}
                  {techInfoResult?.error && (
                    <p className="text-sm text-red-600">{techInfoResult.error}</p>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Print One Sheets */}
            <BulkDocumentUpload onUploaded={fetchData} />

            {/* Print One Sheets */}
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  toast.info("Generating one sheets PDF...");
                  const { downloadFromApi } = await import("../lib/download");
                  await downloadFromApi(`/api/print/one-sheets?status=${filterStatus}`, `student_one_sheets.pdf`);
                  toast.success("One sheets PDF downloaded!");
                } catch { toast.error("Failed to generate one sheets"); }
              }}
              data-testid="print-one-sheets-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
              One Sheets
            </Button>

            {/* Address Labels */}
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  toast.info("Generating address labels PDF...");
                  const { downloadFromApi } = await import("../lib/download");
                  await downloadFromApi(`/api/print/address-labels?status=${filterStatus}`, `address_labels.pdf`);
                  toast.success("Address labels PDF downloaded!");
                } catch { toast.error("Failed to generate address labels"); }
              }}
              data-testid="print-address-labels-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><rect x="1" y="6" width="22" height="12" rx="2"/><path d="M1 10h22"/></svg>
              Address Labels
            </Button>

            {/* Print OTC Medical List */}
            <Button
              variant="outline"
              onClick={handlePrintOtcList}
              data-testid="print-otc-list-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="m10.5 20.5 10-10a2.828 2.828 0 1 0-4-4l-10 10a2.5 2.5 0 0 0 3.5 3.5Z"/><path d="m8.5 8.5 4 4"/></svg>
              OTC Medical List
            </Button>

            {/* Add Student Button */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="add-student-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
                  </svg>
                  Add Student
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Student</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateStudent} className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>First Name *</Label>
                      <Input
                        value={newStudent.first_name}
                        onChange={(e) => setNewStudent((p) => ({ ...p, first_name: e.target.value }))}
                        required
                        data-testid="new-student-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name *</Label>
                      <Input
                        value={newStudent.last_name}
                        onChange={(e) => setNewStudent((p) => ({ ...p, last_name: e.target.value }))}
                        required
                        data-testid="new-student-last-name"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Middle Name</Label>
                      <Input
                        value={newStudent.middle_name}
                        onChange={(e) => setNewStudent((p) => ({ ...p, middle_name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Preferred Name</Label>
                      <Input
                        value={newStudent.preferred_name}
                        onChange={(e) => setNewStudent((p) => ({ ...p, preferred_name: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date of Birth *</Label>
                      <Input
                        type="date"
                        value={newStudent.dob}
                        onChange={(e) => setNewStudent((p) => ({ ...p, dob: e.target.value }))}
                        required
                        data-testid="new-student-dob"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Pronouns</Label>
                      <Select 
                        value={newStudent.pronouns} 
                        onValueChange={(v) => setNewStudent((p) => ({ ...p, pronouns: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select pronouns" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="he/him">He/Him</SelectItem>
                          <SelectItem value="she/her">She/Her</SelectItem>
                          <SelectItem value="they/them">They/Them</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>School District</Label>
                    <Select 
                      value={newStudent.school_district} 
                      onValueChange={(v) => setNewStudent((p) => ({ ...p, school_district: v }))}
                    >
                      <SelectTrigger data-testid="new-student-district">
                        <SelectValue placeholder="Select district" />
                      </SelectTrigger>
                      <SelectContent>
                        {districts.map((d) => (
                          <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Morning Group</Label>
                      <Select 
                        value={newStudent.morning_group} 
                        onValueChange={(v) => setNewStudent((p) => ({ ...p, morning_group: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Group 1</SelectItem>
                          <SelectItem value="2">Group 2</SelectItem>
                          <SelectItem value="3">Group 3</SelectItem>
                          <SelectItem value="Other">Other (1:1)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Afternoon Group</Label>
                      <Select 
                        value={newStudent.afternoon_group} 
                        onValueChange={(v) => setNewStudent((p) => ({ ...p, afternoon_group: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Group 1</SelectItem>
                          <SelectItem value="2">Group 2</SelectItem>
                          <SelectItem value="Other">Other (1:1)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Schedule Type</Label>
                      <Select 
                        value={newStudent.schedule_type || "full_day"} 
                        onValueChange={(v) => setNewStudent((p) => ({ ...p, schedule_type: v }))}
                      >
                        <SelectTrigger data-testid="schedule-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_day">Full Day</SelectItem>
                          <SelectItem value="half_day">Half Day</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-6 pt-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="photos"
                        checked={newStudent.photos_allowed}
                        onCheckedChange={(c) => setNewStudent((p) => ({ ...p, photos_allowed: c }))}
                      />
                      <Label htmlFor="photos" className="cursor-pointer">Photos Allowed</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="otc"
                        checked={newStudent.otc_medications}
                        onCheckedChange={(c) => setNewStudent((p) => ({ ...p, otc_medications: c }))}
                      />
                      <Label htmlFor="otc" className="cursor-pointer">OTC Medications</Label>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="create-student-btn">
                      Create Student
                    </Button>
                  </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
                data-testid="student-search"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]" data-testid="filter-status">
                <SelectValue placeholder="Student Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="current">Current Students</SelectItem>
                <SelectItem value="former">Former Students</SelectItem>
                <SelectItem value="graduated">Graduated (Alumni)</SelectItem>
                <SelectItem value="all">All Students</SelectItem>
              </SelectContent>
            </Select>
            {filterStatus === "graduated" && (
              <Select value={filterGradYear} onValueChange={setFilterGradYear}>
                <SelectTrigger className="w-[150px]" data-testid="filter-grad-year">
                  <SelectValue placeholder="All Years" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {gradYears.map((y) => <SelectItem key={y} value={y}>Class of {y}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={filterDistrict} onValueChange={setFilterDistrict}>
              <SelectTrigger className="w-[200px]" data-testid="filter-district">
                <SelectValue placeholder="All Districts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Districts</SelectItem>
                {districts.map((d) => (
                  <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Students Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredStudents.map((student, index) => (
          <Card
            key={student.id}
            className={`animate-fade-in stagger-${(index % 5) + 1} cursor-pointer card-hover ${student.status === 'former' ? 'opacity-70 border-dashed' : ''}`}
            onClick={() => navigate(`/students/${student.id}`)}
            data-testid={`student-card-${student.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${student.status === 'former' ? 'bg-gray-400' : 'bg-[#00305E]'}`}>
                  {getInitials(student.first_name, student.last_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">
                      {student.preferred_name || student.first_name} {student.last_name}
                    </h3>
                    {student.status === 'former' && student.graduation_status !== 'graduated' && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">Former</span>
                    )}
                    {student.graduation_status === 'graduated' && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium" data-testid={`grad-badge-${student.id}`}>
                        {student.end_date ? `Class of ${student.end_date.slice(0, 4)}` : "Graduated"}
                      </span>
                    )}
                    {student.status !== 'former' && (() => {
                      const sched = student.schedule || {};
                      const days = ['monday','tuesday','wednesday','thursday','friday'];
                      const hasSchedule = days.some(d => sched[d]?.start && sched[d]?.end);
                      return !hasSchedule ? (
                        <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium" data-testid={`schedule-missing-${student.id}`} title="No schedule times set — expected hours will use defaults">
                          No Schedule
                        </span>
                      ) : null;
                    })()}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {(() => {
                      const raw = student.assigned_clinician;
                      const c = clinicians.find((x) => x.id === raw);
                      const clinName = c ? c.name : raw;
                      return (
                        <span data-testid={`student-clinician-${student.id}`} className={clinName ? "font-medium text-foreground" : ""}>
                          {clinName ? `Clinician: ${clinName}` : "No clinician"}
                        </span>
                      );
                    })()}
                    {" • "}{student.school_district || "No district"}
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  </svg>
                  <span className="truncate">
                    {student.first_contact_name || "No parent listed"}
                  </span>
                </div>
                {student.first_contact_phone && (
                  <div className="flex items-center gap-2 text-muted-foreground mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    <span>{student.first_contact_phone}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredStudents.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No students found matching your criteria</p>
        </div>
      )}
    </div>
  );
}
